import type { StampShape } from '../types';

const MIN_COVERAGE = 0.05;
const MAX_COVERAGE = 1.5;
const CUSTOM_THRESHOLD_MAP_SIZE = 65;
const CUSTOM_THRESHOLD_SCAN_STEPS = 96;
const CUSTOM_TONE_EPSILON = 1 / 510;

/**
 * StampThreshold: returns the within-cell threshold at normalised cell
 * coords (u, v) ∈ [0, 1). Higher threshold means the pixel paints
 * "earlier" (i.e. closer to the dot centre); the comparison is
 * `cellGray ≤ threshold ? paint : skip`.
 *
 * The v0.2.1 unification gives every shape the same scaling rule:
 *
 *   threshold(uv) = 1 - min(1, dist(uv) / coverage)
 *
 * where `dist(uv)` is the shape-specific distance from cell centre,
 * normalised so a dot at coverage=1 fills its own enclosing square
 * (corners untouched). At equal coverage the enclosing square is the
 * same across shapes - Pixel / Circle / Diamond / Line / Custom - so
 * the user can swap silhouettes without re-tuning size.
 */
export type StampThreshold = (u: number, v: number) => number;

export type StampTransform = {
  flipX?: boolean;
  flipY?: boolean;
};

/**
 * Build a per-cell threshold function for the unified stamp model.
 *
 * `shapeAngleDeg` rotates the shape within the cell around (0.5, 0.5).
 * It is independent of any grid-level rotation. Pixel and circle are
 * rotation-invariant in their silhouettes (a square pixel and a circle
 * look the same at any cell-rotation), so the wrapper is skipped for
 * those to keep the hot loop tight.
 */
export function buildStampThreshold(
  stamp: StampShape,
  shapeAngleDeg = 0,
  transform: StampTransform = {},
): StampThreshold {
  const inner = buildStampThresholdRaw(stamp);
  const flipX = transform.flipX ?? false;
  const flipY = transform.flipY ?? false;
  if (
    shapeAngleDeg === 0 &&
    !flipX &&
    !flipY
  ) {
    return inner;
  }
  if (!flipX && !flipY && (stamp.type === 'pixel' || stamp.type === 'circle')) {
    return inner;
  }
  const rad = (shapeAngleDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return (u, v) => {
    const du = u - 0.5;
    const dv = v - 0.5;
    let ru = 0.5 + du * cos - dv * sin;
    let rv = 0.5 + du * sin + dv * cos;
    if (flipX) ru = 1 - ru;
    if (flipY) rv = 1 - rv;
    return inner(ru, rv);
  };
}

function buildStampThresholdRaw(stamp: StampShape): StampThreshold {
  switch (stamp.type) {
    case 'pixel': {
      // L∞ distance from centre. coverage=1 fills the cell, coverage=0
      // shrinks the dot to a single pixel at the cell midpoint.
      const cov = clampCoverage(stamp.coverage);
      return (u, v) => {
        const d = Math.max(Math.abs(u - 0.5), Math.abs(v - 0.5)) * 2;
        return 1 - Math.min(1, d / cov);
      };
    }

    case 'circle': {
      // L2 distance from centre, squared so the falloff matches the
      // visual radius. coverage=1 fills the inscribed circle of the
      // cell (corners stay clear); coverage=0 collapses to a point.
      const cov = clampCoverage(stamp.coverage);
      return (u, v) => {
        const du = u - 0.5;
        const dv = v - 0.5;
        const d2 = du * du + dv * dv;
        return 1 - Math.min(1, (d2 * 4) / (cov * cov));
      };
    }

    case 'diamond': {
      // L1 distance from centre.
      const cov = clampCoverage(stamp.coverage);
      return (u, v) => {
        const d = Math.abs(u - 0.5) + Math.abs(v - 0.5);
        return 1 - Math.min(1, (d * 2) / cov);
      };
    }

    case 'line': {
      // Stripe through the cell centre, perpendicular to the v axis
      // (rotation comes from shapeAngleDeg if non-zero). coverage
      // controls thickness as a fraction of the cell. The threshold
      // peaks at the line's centre line and falls off to zero at the
      // edge of the band.
      const cov = clampCoverage(stamp.coverage);
      return (_, v) => {
        const d = Math.abs(v - 0.5) * 2; // 0 at the line, 1 at the cell edge
        return 1 - Math.min(1, d / cov);
      };
    }

    case 'cross': {
      const cov = clampCoverage(stamp.coverage);
      return (u, v) => Math.max(lineDistanceThreshold(v, cov), lineDistanceThreshold(u, cov));
    }

    case 'ellipse': {
      const cov = clampCoverage(stamp.coverage);
      return (u, v) => {
        const du = (u - 0.5) / 0.5;
        const dv = (v - 0.5) / 0.32;
        const d2 = du * du + dv * dv;
        return 1 - Math.min(1, d2 / (cov * cov));
      };
    }

    case 'noise': {
      const cov = clampCoverage(stamp.coverage);
      return (u, v) => {
        const footprint = squareFootprintThreshold(u, v, cov);
        if (footprint <= 0) return 0;
        const x = Math.floor(u * 32);
        const y = Math.floor(v * 32);
        return footprint * hash01(x, y);
      };
    }

    case 'asterisk': {
      const cov = clampCoverage(stamp.coverage);
      return (u, v) =>
        Math.max(
          lineDistanceThreshold(v, cov),
          lineDistanceThreshold(u, cov),
          diagonalLineThreshold(u, v, cov, 1),
          diagonalLineThreshold(u, v, cov, -1),
        );
    }

    case 'flower': {
      // Five perfectly round petals constructed from individual circles
      // to give a plump, daisy-like appearance.
      const cov = clampCoverage(stamp.coverage);
      const petalDist = cov * 0.26;
      const petalRadius = cov * 0.24;
      const centerRadius = cov * 0.22;

      const cx0 = 0.5 + Math.cos(-Math.PI / 2) * petalDist;
      const cy0 = 0.5 + Math.sin(-Math.PI / 2) * petalDist;
      const cx1 = 0.5 + Math.cos(-Math.PI / 2 + (2 * Math.PI) / 5) * petalDist;
      const cy1 = 0.5 + Math.sin(-Math.PI / 2 + (2 * Math.PI) / 5) * petalDist;
      const cx2 = 0.5 + Math.cos(-Math.PI / 2 + (4 * Math.PI) / 5) * petalDist;
      const cy2 = 0.5 + Math.sin(-Math.PI / 2 + (4 * Math.PI) / 5) * petalDist;
      const cx3 = 0.5 + Math.cos(-Math.PI / 2 + (6 * Math.PI) / 5) * petalDist;
      const cy3 = 0.5 + Math.sin(-Math.PI / 2 + (6 * Math.PI) / 5) * petalDist;
      const cx4 = 0.5 + Math.cos(-Math.PI / 2 + (8 * Math.PI) / 5) * petalDist;
      const cy4 = 0.5 + Math.sin(-Math.PI / 2 + (8 * Math.PI) / 5) * petalDist;

      return (u, v) => {
        let maxThresh = circleAt(u, v, 0.5, 0.5, centerRadius);
        maxThresh = Math.max(maxThresh, circleAt(u, v, cx0, cy0, petalRadius));
        maxThresh = Math.max(maxThresh, circleAt(u, v, cx1, cy1, petalRadius));
        maxThresh = Math.max(maxThresh, circleAt(u, v, cx2, cy2, petalRadius));
        maxThresh = Math.max(maxThresh, circleAt(u, v, cx3, cy3, petalRadius));
        maxThresh = Math.max(maxThresh, circleAt(u, v, cx4, cy4, petalRadius));
        return maxThresh;
      };
    }

    case 'star': {
      // True five-pointed star. The radial silhouette interpolates
      // linearly between an outer point (every 72°) and an inner dent
      // (offset 36°). innerRatio sets how deep the dent is - 0.4 is a
      // recognisably star-shaped silhouette without becoming spiky.
      const cov = clampCoverage(stamp.coverage);
      const slice = (2 * Math.PI) / 5;
      const halfSlice = slice / 2;
      const innerRatio = 0.4;
      return (u, v) => {
        const du = u - 0.5;
        const dv = v - 0.5;
        // +PI/2 puts a point at the top of the cell (12 o'clock).
        const angle = Math.atan2(dv, du) + Math.PI / 2;
        const a = ((angle % slice) + slice) % slice;
        const local = a < halfSlice ? a / halfSlice : (slice - a) / halfSlice;
        // local in [0, 1]: 0 = at a point, 1 = at the dent.
        const r = cov * (innerRatio + (1 - innerRatio) * (1 - local));
        const radius = Math.sqrt(du * du + dv * dv) * 2;
        return 1 - Math.min(1, radius / Math.max(MIN_COVERAGE, r));
      };
    }

    case 'heart': {
      const cov = clampCoverage(stamp.coverage);
      return (u, v) => heartThreshold(u, v, cov);
    }

    case 'spade': {
      const cov = clampCoverage(stamp.coverage);
      return (u, v) => {
        const top = heartThreshold(u, 1 - v, cov);
        const stem = Math.min(
          lineDistanceThreshold(u, cov * 0.24),
          Math.max(0, (v - 0.52) / 0.28),
        );
        return Math.max(top, stem);
      };
    }

    case 'club': {
      const cov = clampCoverage(stamp.coverage);
      return (u, v) => {
        const lobes = Math.max(
          circleAt(u, v, 0.5, 0.33, cov * 0.36),
          circleAt(u, v, 0.36, 0.55, cov * 0.34),
          circleAt(u, v, 0.64, 0.55, cov * 0.34),
        );
        const stem = Math.min(
          lineDistanceThreshold(u, cov * 0.22),
          Math.max(0, (v - 0.56) / 0.3),
        );
        return Math.max(lobes, stem);
      };
    }

    case 'custom': {
      const thresholdMap = buildCustomStampThresholdImage(stamp.mask);
      const tw = thresholdMap.width;
      const th = thresholdMap.height;
      const data = thresholdMap.data;
      const cov = clampCoverage(stamp.coverage);
      const halfCov = cov / 2;
      return (u, v) => {
        const du = u - 0.5;
        const dv = v - 0.5;
        if (Math.abs(du) > halfCov || Math.abs(dv) > halfCov) return 0;
        // Map the inset [-halfCov, halfCov] back into [0, 1] to sample
        // the mask.
        const mu = (du + halfCov) / cov;
        const mv = (dv + halfCov) / cov;
        let mx = Math.floor(mu * tw);
        let my = Math.floor(mv * th);
        if (mx < 0) mx = 0;
        else if (mx >= tw) mx = tw - 1;
        if (my < 0) my = 0;
        else if (my >= th) my = th - 1;
        return (data[(my * tw + mx) * 4] ?? 0) / 255;
      };
    }
  }
}

export function buildCustomStampThresholdImage(mask: ImageData): ImageData {
  const out = new ImageData(CUSTOM_THRESHOLD_MAP_SIZE, CUSTOM_THRESHOLD_MAP_SIZE);
  for (let y = 0; y < CUSTOM_THRESHOLD_MAP_SIZE; y++) {
    const v = (y + 0.5) / CUSTOM_THRESHOLD_MAP_SIZE;
    for (let x = 0; x < CUSTOM_THRESHOLD_MAP_SIZE; x++) {
      const u = (x + 0.5) / CUSTOM_THRESHOLD_MAP_SIZE;
      const threshold = customStampThresholdAt(mask, u, v);
      const byte = Math.round(clamp01(threshold) * 255);
      const i = (y * CUSTOM_THRESHOLD_MAP_SIZE + x) * 4;
      out.data[i] = byte;
      out.data[i + 1] = byte;
      out.data[i + 2] = byte;
      out.data[i + 3] = 255;
    }
  }
  return out;
}

function customStampThresholdAt(mask: ImageData, u: number, v: number): number {
  const tone = sampleCustomMaskTone(mask, u, v);
  if (tone <= CUSTOM_TONE_EPSILON) return 0;

  const du = u - 0.5;
  const dv = v - 0.5;
  const radial = Math.max(Math.abs(du), Math.abs(dv)) * 2;
  if (radial <= 0.0001) return tone;

  const dirX = du / (radial / 2);
  const dirY = dv / (radial / 2);
  let support = radial;
  for (let step = 1; step <= CUSTOM_THRESHOLD_SCAN_STEPS; step++) {
    const sr = radial + ((1 - radial) * step) / CUSTOM_THRESHOLD_SCAN_STEPS;
    const su = 0.5 + (dirX * sr) / 2;
    const sv = 0.5 + (dirY * sr) / 2;
    if (sampleCustomMaskTone(mask, su, sv) <= CUSTOM_TONE_EPSILON) break;
    support = sr;
  }

  const edge = 1 - Math.min(1, radial / Math.max(radial, support));
  return tone * edge;
}

function sampleCustomMaskTone(mask: ImageData, u: number, v: number): number {
  let x = Math.floor(u * mask.width);
  let y = Math.floor(v * mask.height);
  if (x < 0) x = 0;
  else if (x >= mask.width) x = mask.width - 1;
  if (y < 0) y = 0;
  else if (y >= mask.height) y = mask.height - 1;

  const i = (y * mask.width + x) * 4;
  const alpha = mask.data[i + 3] ?? 255;
  if (alpha < 128) return 0;
  const r = mask.data[i] ?? 0;
  const g = mask.data[i + 1] ?? r;
  const b = mask.data[i + 2] ?? r;
  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  const tone = clamp01(1 - luma / 255);
  return tone <= CUSTOM_TONE_EPSILON ? 0 : tone;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function clampCoverage(coverage: number): number {
  return Math.min(MAX_COVERAGE, Math.max(MIN_COVERAGE, coverage));
}

function squareFootprintThreshold(u: number, v: number, cov: number): number {
  const d = Math.max(Math.abs(u - 0.5), Math.abs(v - 0.5)) * 2;
  return 1 - Math.min(1, d / cov);
}

function lineDistanceThreshold(axisValue: number, cov: number): number {
  const d = Math.abs(axisValue - 0.5) * 2;
  return 1 - Math.min(1, d / cov);
}

function diagonalLineThreshold(u: number, v: number, cov: number, slope: 1 | -1): number {
  const du = u - 0.5;
  const dv = v - 0.5;
  const d = Math.abs(slope === 1 ? du - dv : du + dv) * Math.SQRT2;
  return 1 - Math.min(1, (d * 2) / cov);
}

function circleAt(u: number, v: number, cx: number, cy: number, radius: number): number {
  const du = u - cx;
  const dv = v - cy;
  const d = Math.sqrt(du * du + dv * dv);
  return 1 - Math.min(1, d / Math.max(MIN_COVERAGE, radius));
}

function heartThreshold(u: number, v: number, cov: number): number {
  const x = ((u - 0.5) * 2) / cov;
  const y = ((0.58 - v) * 2) / cov;
  const a = x * x + y * y - 0.3;
  const heart = a * a * a - x * x * y * y * y;
  return heart <= 0 ? Math.max(0, 1 - Math.sqrt(x * x + y * y) * 0.65) : 0;
}

function hash01(x: number, y: number): number {
  let n =
    Math.imul(x + 0x9e3779b9, 0x85ebca6b) ^ Math.imul(y + 0xc2b2ae35, 0x27d4eb2f);
  n ^= n >>> 15;
  n = Math.imul(n, 0x2c1b3c6d);
  n ^= n >>> 12;
  return ((n >>> 0) & 0xff) / 255;
}
